import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Adapted from Microsoft Elixir-Framework (MIT), v3.0.1164, aa6c10ab1882f8fa20c1da308e6c1fc6fe2fe5e9.
// Source: packages/fx/src/components/Editor/RichText/ElxRichTextEditor.base.tsx. Local adaptations: Bug 38755929.
import React from 'react';
import { Editor, HtmlSanitizer } from 'roosterjs';
import { Rooster, createUpdateContentPlugin, UpdateMode } from 'roosterjs-react';
import { classNamesFunction, css, useTheme } from '@fluentui/react';
import { ElxInputBase, getInputLabelId, getInputDescriptionId, removeUnsupportedReadOnlyProps, useId } from '@elixir/components';
import { ElxRichTextCommandBarPlugin } from './plugins/commandbar/ElxRichTextCommandBarPlugin';
import { ElxReadOnlyPlugin } from './plugins/readonly/ElxReadOnlyPlugin';
import { ElxRichTextCommandBar } from './plugins/commandbar/ElxRichTextCommandBar';
const CLASS = 'elx-richtext-editor';
const getClassNames = classNamesFunction();
export const ElxRichTextEditorBase = (props) => {
    var _a;
    const generatedId = useId(CLASS);
    const id = props.id || generatedId;
    const { value, onChange, readOnly, disabled, buttonItems, ariaLabel } = props;
    const theme = useTheme();
    const commandBarTop = (_a = props.commandBarTop) !== null && _a !== void 0 ? _a : false;
    let contentDiv;
    const commandBarDiv = React.useRef(null);
    const hintRef = React.useRef(null);
    const classNames = getClassNames(props.styles, { theme });
    const { editor, commandBarContainer } = classNames;
    const editorDisabled = disabled ? classNames === null || classNames === void 0 ? void 0 : classNames.editorDisabled : '';
    const commandBarObserver = observeCommandBar(commandBarDiv);
    const commandBarPlugin = React.useMemo(() => new ElxRichTextCommandBarPlugin(), []);
    const readOnlyPlugin = React.useMemo(() => new ElxReadOnlyPlugin(), []);
    const onChangePlugins = React.useMemo(() => {
        if (!onChange) {
            return [];
        }
        return [
            createUpdateContentPlugin(UpdateMode.OnContentChangedEvent, (html) => {
                onChange(html);
            }),
            createUpdateContentPlugin(UpdateMode.OnUserInput, (html) => {
                onChange(html);
            }),
        ];
    }, [onChange]);
    const plugins = [...onChangePlugins, readOnlyPlugin];
    if (props.plugins) {
        plugins.push(...props.plugins);
    }
    // Give caller plugins first refusal on shortcuts before the local fallback.
    plugins.push(commandBarPlugin);
    const editorCreator = React.useCallback((div, options) => {
        const editor = new Editor(div, options);
        contentDiv = div;
        return editor;
    }, []);
    React.useEffect(() => {
        commandBarObserver.observe(contentDiv, {
            attributeFilter: ['style'],
        });
        return () => {
            commandBarObserver.disconnect();
        };
    }, []);
    React.useEffect(() => {
        readOnlyPlugin.setDisabled(disabled);
        readOnlyPlugin.setReadOnly(readOnly);
    }, [disabled, readOnly, readOnlyPlugin]);
    const commandBar = (_jsx("div", Object.assign({ ref: commandBarDiv, className: commandBarContainer }, { children: _jsx(ElxRichTextCommandBar, { plugin: commandBarPlugin, readOnly: readOnly, disabled: disabled, buttonItems: buttonItems }) })));
    return (_jsxs(ElxInputBase, Object.assign({ id: id, hintRef: hintRef }, removeUnsupportedReadOnlyProps(props), { styles: props.wrapperStyles }, { children: [commandBarTop && commandBar, _jsx(Rooster, { doNotAdjustEditorColor: true, plugins: plugins, "aria-label": ariaLabel, className: css(CLASS, editor, editorDisabled), "aria-labelledby": getInputLabelId(id), "aria-describedby": getInputDescriptionId(id), role: "textbox", initialContent: HtmlSanitizer.convertInlineCss(value || ''), editorCreator: editorCreator, onContextMenu: (e) => readOnlyPlugin.onContextMenu(e), onFocus: () => {
                    var _a;
                    (_a = hintRef.current) === null || _a === void 0 ? void 0 : _a.show();
                }, onBlur: (e) => {
                    var _a;
                    props.onBlur && props.onBlur(e);
                    (_a = hintRef.current) === null || _a === void 0 ? void 0 : _a.dismiss();
                }, contentEditable: !readOnly && !disabled }), !commandBarTop && commandBar] })));
};
function observeCommandBar(commandBarDiv) {
    return new window.MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            // copy the width from the editor and apply it to the command bar
            const newStyle = mutation.target.style;
            commandBarDiv.current.style.width = newStyle.width;
        });
        // fire the resize event to resize the command bar to fit its container
        window.dispatchEvent(new Event('resize'));
    });
}
